import * as i18nInterface from '../_config/i18n/i18n-interface';
import * as navConstants from '../routing/routing.const';

import { HttpErrorResponse, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { SpxStorage, SpxStorageKeyEnum } from '@softpak/components/spx-storage';

import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { SharedStorageKeyEnum } from '../_config/storage/shared-storage-key-enum';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { captureException } from '@sentry/angular';
import { catchError } from 'rxjs/operators';
import { isStatsApiEndpoint } from '../_config/stats-api-endpoints.const';
import { spxSpinnerActions } from '@softpak/components/spx-spinner';
import { spxToasterActions } from '@softpak/components/spx-toaster';

@Injectable()
export class SharedHttpInterceptor implements HttpInterceptor {
    private appStore = inject(Store);
    private router = inject(Router);
    private translateService = inject(TranslateService);


    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
        return next.handle(req.clone({
            headers: req.headers.append(
                'Authorization',
                `oecp ${SpxStorage.getChannelSetting<SharedStorageKeyEnum>(SpxStorageKeyEnum.token)}`)
        })).pipe(
            catchError((err: any) => {
                if (err instanceof HttpErrorResponse) {
                    const shouldLogError = !isStatsApiEndpoint(req.url);

                    if (req.url.includes(`j_spring_security_check`) && err.error?.error === 'sso.token.unauthorized_client') {
                        this.displayWarning('CREDENTIALS', this.translateService.instant(i18nInterface.textErrorWrongCredentials));
                    } else {
                        switch (err.status) {
                            case 0:
                                if (shouldLogError) {
                                    this.log(`${err.status} ${i18nInterface.textErrorDeviceIsOffline}: ${req.url}`);
                                }
                                return throwError(() => ({ id: 0, autoclose: 5000, message: this.translateService.instant(i18nInterface.textErrorDeviceIsOffline) }));
                            case 401:
                            case 403:
                                this.appStore.dispatch(spxSpinnerActions.reset());
                                this.appStore.dispatch(spxToasterActions.clearAll());
                                this.displayWarning('EXPIRED', this.translateService.instant(i18nInterface.textErrorTokenExpired));
                                break;
                            case 404:
                                this.displayErrorAndLog(this.translateService.instant(i18nInterface.textErrorEndpointNotFound), `Status ${err.status} ${err.statusText} ${err.name}: ${req.url}`, shouldLogError);
                                break;
                            case 500:
                                this.displayErrorAndLog(this.translateService.instant(i18nInterface.textErrorUnexpectedInBackend), `Status ${err.status} ${err.statusText} ${err.name}: ${req.url}`, shouldLogError);
                                break;
                            case 501:
                            case 502:
                                this.displayErrorAndLog(this.translateService.instant(i18nInterface.textErrorNotImplemented), `Status ${err.status} ${err.statusText} ${err.name}: ${req.url}`, shouldLogError);
                                break;
                            default:
                                this.displayErrorAndLog(this.translateService.instant(i18nInterface.textErrorHttpFailure), `Status: ${err.status} ${err.statusText} ${err.name}: ${req.url}`, shouldLogError);
                                break;
                        }

                        if (err.status === 401 || err.status === 403) {
                            this.router.navigate([navConstants.navOutUrl]);
                        } else if (err.status >= 1) {
                            this.router.navigate([navConstants.navHmeUrl]);
                        }

                        return of([]);
                    }

                    return throwError(() => err);
                }
            })
        );
    }

    private displayWarning(uniqueIdentifier: string, messageText: string): void {
        this.appStore.dispatch(spxToasterActions.createWarning({
            messageText,
            // uniqueIdentifier,
        }));
    }

    private displayErrorAndLog(userFriendlyMessage: string, fullMessage: string, shouldLogError = true): void {
        if (shouldLogError) {
            captureException(`[${SpxStorage.getSetting<SharedStorageKeyEnum>(SpxStorageKeyEnum.brand)} / HTTP]: ${fullMessage}`, { level: 'fatal' });
        }
        this.appStore.dispatch(spxToasterActions.clearAll());
        this.appStore.dispatch(spxToasterActions.createError({
            messageText: userFriendlyMessage,
        }));
    }

    private log(fullMessage: string): void {
        captureException(fullMessage, { level: 'warning' });
    }
}
